import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import axiosInstance from "@/lib/axios";
import { leadsEndpoints } from "@/config/endpoints";
import { useDebounce } from "@/hooks/useDebounce";
import {
  invalidateLeadBoard,
  invalidateLeadCaches,
  invalidateLeadDetail,
  invalidateLeadTimeline,
  invalidateRecentActivityFeed,
  queryClient,
} from "@/lib/queryClient";
import { invalidateDuplicateLeads } from "./useDuplicates";
import { PHONE_CHECK_KEY } from "./useCheckLeadPhone";

/**
 * MERGE TWO LEADS (owner, 2026-10-19: "a proper merging option… one is
 * whatsapp number one is main number… ask which counsellor to choose").
 * Server: GET /core/leads/:id/merge-preview, POST /core/leads/:id/merge
 * (`core/leads/merge/lead.merge.service.ts`). `:id` is the lead KEPT.
 */

export type MergeSide = "keep" | "other";
export type NumberSlot = "keep.phone" | "keep.whatsapp" | "other.phone" | "other.whatsapp";

export interface LeadMergeChoices {
  name: MergeSide;
  email: MergeSide;
  location: MergeSide;
  branch: MergeSide;
  stage: MergeSide;
  source: MergeSide;
  phone: NumberSlot;
  whatsapp: NumberSlot | "none";
  holders: Record<number, number>;
}

interface Named {
  id: number;
  name: string;
}

export interface MergeLeadSide {
  id: number;
  name: string | null;
  email: string | null;
  location: string | null;
  phone: string | null;
  phoneCountryCode: string | null;
  whatsapp: string | null;
  createdAt: string;
  isArchived: boolean;
  reenquired: number;
  branch: Named | null;
  stage: Named | null;
  status: Named | null;
  subStatus: Named | null;
  source: Named | null;
  campaign: Named | null;
}

export interface LeadMergePreview {
  keep: MergeLeadSide;
  other: MergeLeadSide;
  numbers: { slot: NumberSlot; display: string; valid: boolean }[];
  roles: {
    roleId: number;
    roleName: string;
    keep: { userId: number; name: string | null } | null;
    other: { userId: number; name: string | null } | null;
    keepFollowUp: string | null;
    otherFollowUp: string | null;
  }[];
  /** `${stageSide}_${branchSide}` → null when compatible, else why not. */
  stageBranch: Record<"keep_keep" | "keep_other" | "other_keep" | "other_other", string | null>;
  moves: { deals: number; notes: number; followUps: number; calls: number; chats: number; activity: number };
  defaults: LeadMergeChoices;
}

export interface MergeSearchLead {
  id: number;
  name: string;
  phone: string;
  stage?: string;
  status?: string;
  branch?: string;
}

/** Leads the user can see, by name / phone, for the "merge with" picker. */
export function useMergeLeadSearch(search: string, excludeId: number | null) {
  const debounced = useDebounce(search.trim(), 300);
  const query = useQuery({
    queryKey: ["merge-lead-search", debounced],
    queryFn: async (): Promise<MergeSearchLead[]> => {
      const res = await axiosInstance.get(leadsEndpoints, {
        params: { search: debounced, limit: 10, page: 1 },
      });
      const raw = res.data?.leads || res.data?.data || [];
      return raw.map((l: any) => ({
        id: l.id,
        name: l.name || "Unnamed",
        phone: [l.phoneCountryCode, l.phone].filter(Boolean).join(" "),
        stage: l.stage?.name,
        status: l.status?.name,
        branch: l.branch?.name,
      }));
    },
    enabled: debounced.length >= 2,
    staleTime: 15_000,
  });
  return {
    leads: (query.data ?? []).filter((l) => l.id !== excludeId),
    isSearching: (query.isLoading && query.fetchStatus !== "idle") || search.trim() !== debounced,
  };
}

export function useLeadMergePreview(keepId: number | null, otherId: number | null) {
  return useQuery({
    queryKey: ["lead-merge-preview", keepId, otherId],
    queryFn: async (): Promise<LeadMergePreview> => {
      const res = await axiosInstance.get(`${leadsEndpoints}/${keepId}/merge-preview`, {
        params: { otherId },
      });
      return res.data.preview;
    },
    enabled: keepId != null && otherId != null && keepId !== otherId,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useMergeLeads() {
  return useMutation({
    mutationFn: async (vars: { keepId: number; otherId: number; choices: LeadMergeChoices }) => {
      const res = await axiosInstance.post(
        `${leadsEndpoints}/${vars.keepId}/merge`,
        { otherId: vars.otherId, choices: vars.choices },
        { timeout: 90_000 },
      );
      return res.data as { leadId: number; mergedLeadId: number; moved: Record<string, number> };
    },
    onSuccess: (data, vars) => {
      invalidateLeadCaches();
      invalidateLeadBoard();
      invalidateLeadDetail(vars.keepId);
      invalidateLeadDetail(vars.otherId);
      invalidateLeadTimeline(vars.keepId);
      invalidateRecentActivityFeed();
      invalidateDuplicateLeads(queryClient);
      queryClient.invalidateQueries({ queryKey: [PHONE_CHECK_KEY] });
      toast.success(`Lead #${data.mergedLeadId} merged into #${data.leadId}`);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Couldn't merge these leads");
    },
  });
}
